import React, { useEffect, useState } from 'react';
import useScrollLock from '../../../hooks/useScrollLock';
import { storage } from '../../../utils/helpers';
import { useAppSelector } from '../../../store/hooks';
import api from '../../../api';

interface SessionCompletionModalProps {
  open: boolean;
  onClose: () => void;
  onStartAnother?: () => void;
  duration?: number;
  goal?: string;
}

type FeedbackEmoji = 'focused' | 'okay' | 'distracting' | null;

const SessionCompletionModal: React.FC<SessionCompletionModalProps> = ({
  open,
  onClose,
  onStartAnother,
  duration = 25,
  goal = 'Focus Session'
}) => {
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackEmoji>(null);
  const [improvementFeedback, setImprovementFeedback] = useState('');
  const [showDailyVariant, setShowDailyVariant] = useState(false);
  const [step, setStep] = useState<'regular' | 'daily'>('regular');

  const { user } = useAppSelector((state) => state.user);

  // Check if daily variant should be shown
  useEffect(() => {
    if (open) {
      const today = new Date().toDateString();
      const lastDailyFeedbackDate = storage.get<string>('lastDailyFeedbackDate');

      if (lastDailyFeedbackDate !== today) {
        // Show regular first, then daily
        setStep('regular');
        setShowDailyVariant(true);
      } else {
        // Only show regular variant
        setStep('regular');
        setShowDailyVariant(false);
      }
    }
  }, [open]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setSelectedFeedback(null);
      setImprovementFeedback('');
      setStep('regular');
    }
  }, [open]);

  const handleFeedbackSelect = (feedback: FeedbackEmoji) => {
    setSelectedFeedback(feedback);
  };

  const submitSessionFeedback = async () => {
    if (!selectedFeedback && !improvementFeedback) return;

    // Construct message
    let message = `Session Feedback: ${goal}\n`;
    if (selectedFeedback) {
      message += `Feeling: ${selectedFeedback}\n`;
    }
    if (improvementFeedback) {
      message += `Improvement: ${improvementFeedback}`;
    }

    try {
      await api.feedbacks.submitFeedback({
        name: user?.name || 'Anonymous User',
        email: user?.email || 'anonymous@example.com',
        message: message
      });
      // Optional: Store that we sent feedback to avoid spamming if logic requires
    } catch (error) {
      console.error('Failed to submit session feedback', error);
      // We silently fail as to not disturb the user flow for feedback
    }
  };

  const handleDone = () => {
    if (showDailyVariant && step === 'regular') {
      // Move to daily variant
      setStep('daily');
    } else {
      // Close modal logic
      if (step === 'daily') {
        const today = new Date().toDateString();
        storage.set('lastDailyFeedbackDate', today);
      }

      // Submit feedback if any
      if (selectedFeedback || improvementFeedback) {
        submitSessionFeedback();
      }

      onClose();
    }
  };

  const handleStartAnother = () => {
    // If starting another, we submit what we have so far (usually just regular step feedback)
    if (selectedFeedback) {
      submitSessionFeedback();
    }

    if (onStartAnother) {
      onStartAnother();
    } else {
      onClose();
    }
  };

  // Prevent body scroll when modal is open
  useScrollLock(open);

  if (!open) return null;

  const formatDuration = (minutes: number) => {
    if (minutes < 60) {
      return `${minutes} minutes`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours} hour${hours > 1 ? 's' : ''} ${mins > 0 ? `${mins} minute${mins > 1 ? 's' : ''}` : ''}`;
  };

  // Regular variant
  if (step === 'regular') {
    return (
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-[#FAFAFA] rounded-xl p-5 sm:p-6 max-w-md w-full border border-gray-200/60 shadow-lg">
          <div className="text-center space-y-5">
            {/* Title */}
            <h2 className="text-2xl sm:text-3xl font-medium text-gray-900">
              Nice work.
            </h2>

            {/* Summary */}
            <div className="space-y-1.5">
              <p className="text-base sm:text-lg text-gray-700 font-medium">
                Focus session completed
              </p>
              <p className="text-sm sm:text-base text-gray-600">
                Duration: {formatDuration(duration)}
              </p>
            </div>

            {/* Question */}
            <div className="space-y-4">
              <p className="text-base sm:text-lg text-gray-700 font-medium">
                How did this session feel?
              </p>

              {/* Emoji Options */}
              <div className="grid grid-cols-3 gap-4 sm:gap-5 w-full">
                <button
                  onClick={() => handleFeedbackSelect('focused')}
                  className={`flex flex-col items-center gap-2 p-3.5 sm:p-4 rounded-xl transition-all w-full ${selectedFeedback === 'focused'
                    ? 'bg-orange-50 border-2 border-orange-600'
                    : 'bg-white border border-gray-200 hover:border-orange-300'
                    }`}
                >
                  <span className="text-3xl sm:text-4xl">🙂</span>
                  <span className={`text-sm sm:text-base font-medium ${selectedFeedback === 'focused' ? 'text-orange-600' : 'text-gray-700'
                    }`}>
                    Focused
                  </span>
                </button>

                <button
                  onClick={() => handleFeedbackSelect('okay')}
                  className={`flex flex-col items-center gap-2 p-3.5 sm:p-4 rounded-xl transition-all w-full ${selectedFeedback === 'okay'
                    ? 'bg-orange-50 border-2 border-orange-600'
                    : 'bg-white border border-gray-200 hover:border-orange-300'
                    }`}
                >
                  <span className="text-3xl sm:text-4xl">😐</span>
                  <span className={`text-sm sm:text-base font-medium ${selectedFeedback === 'okay' ? 'text-orange-600' : 'text-gray-700'
                    }`}>
                    Okay
                  </span>
                </button>

                <button
                  onClick={() => handleFeedbackSelect('distracting')}
                  className={`flex flex-col items-center gap-2 p-3.5 sm:p-4 rounded-xl transition-all w-full ${selectedFeedback === 'distracting'
                    ? 'bg-orange-50 border-2 border-orange-600'
                    : 'bg-white border border-gray-200 hover:border-orange-300'
                    }`}
                >
                  <span className="text-3xl sm:text-4xl">🙁</span>
                  <span className={`text-sm sm:text-base font-medium ${selectedFeedback === 'distracting' ? 'text-orange-600' : 'text-gray-700'
                    }`}>
                    Distracting
                  </span>
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-2.5 pt-1.5">
              <button
                onClick={handleDone}
                className="flex-1 px-5 py-2.5 sm:py-3 bg-white border border-orange-600 hover:bg-orange-50 text-orange-600 rounded-lg font-medium transition-colors text-sm sm:text-base"
              >
                Done
              </button>
              <button
                onClick={handleStartAnother}
                className="flex-1 px-5 py-2.5 sm:py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-colors text-sm sm:text-base"
              >
                Start another focus session
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Daily variant
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[#FAFAFA] rounded-xl p-5 sm:p-6 max-w-md w-full border border-gray-200/60 shadow-lg">
        <div className="space-y-5">
          {/* Prompt */}
          <h3 className="text-lg sm:text-xl font-medium text-gray-900 text-center">
            Anything we could improve for you?<span className="text-gray-500 font-normal"> (Optional)</span>
          </h3>

          {/* Textarea */}
          <textarea
            value={improvementFeedback}
            onChange={(e) => setImprovementFeedback(e.target.value)}
            placeholder="Something felt distracting…"
            rows={3}
            className="w-full px-4 py-2.5 bg-white border border-gray-200/60 rounded-lg text-gray-900 placeholder:text-gray-400 focus:ring-1 focus:ring-orange-400/30 focus:border-orange-300/50 focus:outline-none resize-none text-sm sm:text-base"
          />

          {/* Action Button */}
          <button
            onClick={handleDone}
            className="w-full px-5 py-2.5 sm:py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-colors text-sm sm:text-base"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionCompletionModal;
